import dayjs, { Dayjs, ConfigType } from "dayjs";
import { IModel, IIdentifiable } from "@/loader";

export interface IWPPost {
	id?: number;
	ID?: number;
	post_author?: number | null;
	post_date?: ConfigType | null;
	post_date_gmt?: ConfigType | null;
	post_content?: string | null;
	post_title?: string | null;
	post_excerpt?: string | null;
	post_status?: string;
	comment_status?: string | null;
	ping_status?: string | null;
	post_password?: string | null;
	post_name?: string | null;
	to_ping?: string | null;
	pinged?: string | null;
	post_modified?: ConfigType | null;
	post_modified_gmt?: ConfigType | null;
	post_content_filtered?: string | null;
	post_parent?: number | null;
	guid?: string | null;
	menu_order?: number | null;
	post_type?: string;
	post_mime_type?: string | null;
	comment_count?: number | null;
	url?: string | null;
	edit_url?: string | null;
}

export interface IWPPostOptions {
	id?: boolean;
	ID?: boolean;
	post_author?: boolean;
	post_date?: boolean;
	post_date_gmt?: boolean;
	post_content?: boolean;
	post_title?: boolean;
	post_excerpt?: boolean;
	post_status?: boolean;
	comment_status?: boolean;
	ping_status?: boolean;
	post_password?: boolean;
	post_name?: boolean;
	to_ping?: boolean;
	pinged?: boolean;
	post_modified?: boolean;
	post_modified_gmt?: boolean;
	post_content_filtered?: boolean;
	post_parent?: boolean;
	guid?: boolean;
	menu_order?: boolean;
	post_type?: boolean;
	post_mime_type?: boolean;
	comment_count?: boolean;
	url?: boolean;
	edit_url?: boolean;
}

export class WPPost implements IIdentifiable, IWPPost, IModel<IWPPost> {
	public id!: number;
	public post_author!: number | null;
	public post_date!: Dayjs | null;
	public post_date_gmt!: Dayjs | null;
	public post_content!: string | null;
	public post_title!: string | null;
	public post_excerpt!: string | null;
	public post_status!: string;
	public comment_status!: string | null;
	public ping_status!: string | null;
	public post_password!: string | null;
	public post_name!: string | null;
	public to_ping!: string | null;
	public pinged!: string | null;
	public post_modified!: Dayjs | null;
	public post_modified_gmt!: Dayjs | null;
	public post_content_filtered!: string | null;
	public post_parent!: number | null;
	public guid!: string | null;
	public menu_order!: number | null;
	public post_type!: string;
	public post_mime_type!: string | null;
	public comment_count!: number | null;
	public url!: string | null;
	public edit_url!: string | null;

	public get ID(): number {
		return this.id;
	}

	public set ID(value: number) {
		this.id = value;
	}

	public get uniqId(): string {
		return "post" + this.id;
	}

	public get name(): string | null {
		return this.post_title;
	}

	public constructor(data: IWPPost = {}) {
		this.load(data);
	}

	public load(data: IWPPost) {
		this.id = Number.orDefault(data.id || data.ID);
		this.post_author = Number.nullable(data.post_author);
		this.post_date = dayjs.nullable(data.post_date);
		this.post_date_gmt = dayjs.nullable(data.post_date_gmt);
		this.post_content = String.nullable(data.post_content);
		this.post_title = String.nullable(data.post_title);
		this.post_excerpt = String.nullable(data.post_excerpt);
		this.post_status = String.orDefault(data.post_status);
		this.comment_status = String.nullable(data.comment_status);
		this.ping_status = String.nullable(data.ping_status);
		this.post_password = String.nullable(data.post_password);
		this.post_name = String.nullable(data.post_name);
		this.to_ping = String.nullable(data.to_ping);
		this.pinged = String.nullable(data.pinged);
		this.post_modified = dayjs.nullable(data.post_modified);
		this.post_modified_gmt = dayjs.nullable(data.post_modified_gmt);
		this.post_content_filtered = String.nullable(data.post_content_filtered);
		this.post_parent = Number.nullable(data.post_parent);
		this.guid = String.nullable(data.guid);
		this.menu_order = Number.nullable(data.menu_order);
		this.post_type = String.orDefault(data.post_type);
		this.post_mime_type = String.nullable(data.post_mime_type);
		this.comment_count = Number.nullable(data.comment_count);
		this.url = String.nullable(data.url);
		this.edit_url = String.nullable(data.edit_url);
	}

	public update(data: IWPPost) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.ID !== undefined) {
			this.id = Number.orDefault(data.ID);
		}
		if (data.post_author !== undefined) {
			this.post_author = Number.nullable(data.post_author);
		}
		if (data.post_date !== undefined) {
			this.post_date = dayjs.nullable(data.post_date);
		}
		if (data.post_date_gmt !== undefined) {
			this.post_date_gmt = dayjs.nullable(data.post_date_gmt);
		}
		if (data.post_content !== undefined) {
			this.post_content = String.nullable(data.post_content);
		}
		if (data.post_title !== undefined) {
			this.post_title = String.nullable(data.post_title);
		}
		if (data.post_excerpt !== undefined) {
			this.post_excerpt = String.nullable(data.post_excerpt);
		}
		if (data.post_status !== undefined) {
			this.post_status = String.orDefault(data.post_status);
		}
		if (data.comment_status !== undefined) {
			this.comment_status = String.nullable(data.comment_status);
		}
		if (data.ping_status !== undefined) {
			this.ping_status = String.nullable(data.ping_status);
		}
		if (data.post_password !== undefined) {
			this.post_password = String.nullable(data.post_password);
		}
		if (data.post_name !== undefined) {
			this.post_name = String.nullable(data.post_name);
		}
		if (data.to_ping !== undefined) {
			this.to_ping = String.nullable(data.to_ping);
		}
		if (data.pinged !== undefined) {
			this.pinged = String.nullable(data.pinged);
		}
		if (data.post_modified !== undefined) {
			this.post_modified = dayjs.nullable(data.post_modified);
		}
		if (data.post_modified_gmt !== undefined) {
			this.post_modified_gmt = dayjs.nullable(data.post_modified_gmt);
		}
		if (data.post_content_filtered !== undefined) {
			this.post_content_filtered = String.nullable(data.post_content_filtered);
		}
		if (data.post_parent !== undefined) {
			this.post_parent = Number.nullable(data.post_parent);
		}
		if (data.guid !== undefined) {
			this.guid = String.nullable(data.guid);
		}
		if (data.menu_order !== undefined) {
			this.menu_order = Number.nullable(data.menu_order);
		}
		if (data.post_type !== undefined) {
			this.post_type = String.orDefault(data.post_type);
		}
		if (data.post_mime_type !== undefined) {
			this.post_mime_type = String.nullable(data.post_mime_type);
		}
		if (data.comment_count !== undefined) {
			this.comment_count = Number.nullable(data.comment_count);
		}
		if (data.url !== undefined) {
			this.url = String.nullable(data.url);
		}
		if (data.edit_url !== undefined) {
			this.edit_url = String.nullable(data.edit_url);
		}
	}

	public static compareByTitle(a: IWPPost, b : IWPPost): number {
		return a?.post_title?.localeCompare(b?.post_title || "") || -1;
	}
}
